import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ROUTES } from 'Constants';
import { markNotificationRead } from 'Services/Api/_HumanWritten/NotificationLinkService';
import alertToast from 'Util/ToastifyUtils';
import { LottieSpinner } from '../Lottie/LottieSpinner';

/**
 * Notification types this route knows how to redirect. Values mirror the backend
 * `NotificationEntity.NotificationType` discriminators embedded in emailed links.
 */
export const NOTIFICATION_TYPES = {
	userDisplayNameRequest: 'UserDisplayNameRequest',
} as const;

/**
 * Landing component for emailed notification links
 * (/notification/:notificationId?type=...&userId=...). The emailed URL stays a dumb pointer;
 * the destination is decided entirely from its query params — `type` picks the destination
 * shape and the remaining params (currently just userId) fill it in. A read receipt for the
 * notification id is posted fire-and-forget on the way through.
 */
function NotificationRedirect() {
	const navigate = useNavigate();
	const { notificationId } = useParams<{ notificationId: string }>();
	const [searchParams] = useSearchParams();
	const type = searchParams.get('type');
	const userId = searchParams.get('userId');

	React.useEffect(() => {
		if (notificationId) {
			markNotificationRead(notificationId);
		}

		switch (type) {
			case NOTIFICATION_TYPES.userDisplayNameRequest:
				if (userId) {
					navigate(ROUTES.USER_DETAIL.url(userId, 'account'), { replace: true });
					return;
				}
				break;
			default:
				break;
		}

		// Unknown/missing type, or a type missing its expected params — nowhere better to go.
		alertToast('The notification could not be opened', 'error');
		navigate('/home', { replace: true });
	}, [notificationId, type, userId, navigate]);

	return <LottieSpinner />;
}

export default NotificationRedirect;
